// Tablero del equipo: tareas internas (videos, artes, campañas) con responsable, fechas y estado.
// Todas las llamadas a /api/v1/work-items pasan por aquí.
import type {
  Page, Role, UserRef, UserView, WorkItemCommentView, WorkItemDetailView, WorkItemPriority, WorkItemStatus, WorkItemSummaryView,
  WorkItemView,
} from '@hilo/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { api, ApiError } from './api';

export interface WorkFilters {
  /** `me`, `none` o el id de una persona. */
  assignee?: string;
  project?: string;
  q?: string;
}

export interface WorkItemInput {
  title: string;
  description?: string;
  status?: WorkItemStatus;
  priority?: WorkItemPriority;
  project?: string | null;
  assigneeId?: string | null;
  dueDate?: string | null;
  publishDate?: string | null;
}

export type WorkItemChanges = Partial<WorkItemInput>;

export const workKeys = {
  all: ['work-items'] as const,
  detail: (id: string) => ['work-items', 'detail', id] as const,
  column: (status: WorkItemStatus, filters: WorkFilters, focus: WorkFocus | null, since: string | null) =>
    ['work-items', 'column', status, filters, focus, since] as const,
  summary: (filters: WorkFilters, focus: WorkFocus | null, since: string | null) => ['work-items', 'summary', filters, focus, since] as const,
  assignees: ['work-items', 'assignees'] as const,
  projects: ['work-items', 'projects'] as const,
};

/** Tras cualquier cambio o aviso en vivo: columnas, resumen, detalle y proyectos se vuelven a pedir. */
export const refreshWork = (client: QueryClient) => client.invalidateQueries({ queryKey: workKeys.all });

export const fetchWorkItem = (id: string) => api<WorkItemDetailView>('GET', `/work-items/${id}`);
export const fetchAssignees = () => api<UserRef[]>('GET', '/work-items/assignees');
export const fetchProjects = () => api<string[]>('GET', '/work-items/projects');
export const createWorkItem = (input: WorkItemInput) => api<WorkItemDetailView>('POST', '/work-items', { body: input });
/** `expectedRevision` es la revisión que se ve: si otra persona la cambió antes, la API responde 409 REVISION_CONFLICT. */
export const updateWorkItem = (id: string, expectedRevision: number, changes: WorkItemChanges) =>
  api<WorkItemDetailView>('PATCH', `/work-items/${id}`, { body: { expectedRevision, ...changes } });
export const deleteWorkItem = (id: string) => api<void>('DELETE', `/work-items/${id}`);
export const addWorkComment = (id: string, body: string) => api<WorkItemCommentView>('POST', `/work-items/${id}/comments`, { body: { body } });
export const deleteWorkComment = (id: string, commentId: string) => api<void>('DELETE', `/work-items/${id}/comments/${commentId}`);

export const isRevisionConflict = (error: unknown) => error instanceof ApiError && error.code === 'REVISION_CONFLICT';

// ---------------------------------------------------------------------------
// Textos y permisos (mismas reglas que la API: ADMIN y SUPERVISOR gestionan; AGENT mueve solo lo suyo).
// ---------------------------------------------------------------------------
export const STATUS_LABEL: Record<WorkItemStatus, string> = {
  TODO: 'Por hacer',
  IN_PROGRESS: 'En desarrollo',
  IN_REVIEW: 'En revisión',
  DONE: 'Completado',
};

export const STATUS_HINT: Record<WorkItemStatus, string> = {
  TODO: 'Asignado, sin empezar',
  IN_PROGRESS: 'Alguien está trabajando en ello',
  IN_REVIEW: 'Terminando o esperando visto bueno',
  DONE: 'Entregado',
};

export const PRIORITY_LABEL: Record<WorkItemPriority, string> = {
  LOW: 'Baja',
  NORMAL: 'Normal',
  HIGH: 'Alta',
  URGENT: 'Urgente',
};

export const canManageWork = (role: Role) => role !== 'AGENT';
export const canMoveWork = (user: Pick<UserView, 'id' | 'role'>, item: Pick<WorkItemView, 'assignee'>) =>
  canManageWork(user.role) || item.assignee?.id === user.id;
export const canDeleteWork = (role: Role) => role === 'ADMIN';
export const canDeleteComment = (user: Pick<UserView, 'id' | 'role'>, comment: WorkItemCommentView) =>
  user.role === 'ADMIN' || comment.author.id === user.id;

// ---------------------------------------------------------------------------
// Fechas de calendario `YYYY-MM-DD` (sin hora): se comparan con el día de hoy en la zona de la persona.
// ---------------------------------------------------------------------------
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

const DAY_MS = 86_400_000;
const asUtc = (day: string) => Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));

/** Días desde hoy hasta `day` (negativo si ya pasó). */
export const daysUntil = (day: string, today: string) => Math.round((asUtc(day) - asUtc(today)) / DAY_MS);

/** «hoy», «mañana», «ayer» o «3 oct» (con año si no es el actual). */
export function formatDay(day: string, today: string): string {
  const diff = daysUntil(day, today);
  if (diff === 0) return 'hoy';
  if (diff === 1) return 'mañana';
  if (diff === -1) return 'ayer';
  const sameYear = day.slice(0, 4) === today.slice(0, 4);
  return new Intl.DateTimeFormat('es-SV', { timeZone: 'UTC', day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) })
    .format(new Date(asUtc(day))).replace('.', '');
}

export type DueTone = 'overdue' | 'today' | 'soon' | 'later';

/** Cómo de urgente es una entrega; las completadas no se marcan. */
export function dueTone(item: Pick<WorkItemView, 'dueDate' | 'status'>, today: string): DueTone | null {
  if (!item.dueDate || item.status === 'DONE') return null;
  const diff = daysUntil(item.dueDate, today);
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff <= 2) return 'soon';
  return 'later';
}

// ---------------------------------------------------------------------------
// Escala: cada columna pide sus tareas por páginas y los totales llegan aparte (resumen), así el tablero
// pinta como mucho unas decenas de tarjetas aunque haya millones.
// ---------------------------------------------------------------------------
export type WorkFocus = 'overdue' | 'dueWeek' | 'publishWeek';

export const COLUMN_PAGE = 30;
/** «Completado» muestra por defecto solo lo de estos últimos días. */
export const RECENT_DONE_DAYS = 14;

export const addDays = (day: string, days: number) => new Date(asUtc(day) + days * DAY_MS).toISOString().slice(0, 10);

export interface ColumnQuery {
  focus: WorkFocus | null;
  completedSince: string | null;
  cursor: string | null;
}

export const fetchColumn = (status: WorkItemStatus, filters: WorkFilters, options: ColumnQuery, signal?: AbortSignal) =>
  api<Page<WorkItemView>>('GET', '/work-items/page', {
    query: { ...filters, status, limit: COLUMN_PAGE, cursor: options.cursor, focus: options.focus, completedSince: status === 'DONE' ? options.completedSince : null },
    signal,
  });

export const fetchSummary = (filters: WorkFilters, focus: WorkFocus | null, completedSince: string | null) =>
  api<WorkItemSummaryView>('GET', '/work-items/summary', { query: { ...filters, focus, completedSince } });
